import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { MdOutlineDeleteOutline } from "react-icons/md";

const UlgMapVisualization = ({
    indicators,
    locations,
    onSubmit,
    localSelectedLocations,
    setLocalSelectedLocations,
    chartType,
    setChartType,
    typeIndicator,
    setTypeIndicator,
    typeSubIndicators,
    setTypeSubIndicators,
    selectedTypeSubIndicator,
    setSelectedTypeSubIndicator
}) => {
    // ✅ Get Redux state for restoration
    const reduxUlgMapConfig = useSelector(state => state.ulgMapConfig);

    // Local state only for UI control
    const [isBool, setBool] = useState(true);
    const [isUlgMapSubmitDisabled, setIsUlgMapSubmitDisabled] = useState(true);
    const [isRestored, setIsRestored] = useState(false);

    // Helper function for geo level labels
    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State / UT";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    const noOptionsMessage = () => "No compatible indicators found in your workspace.";

    // ✅ Restore from Redux on component mount
    useEffect(() => {
        if (!reduxUlgMapConfig || isRestored) return;
        if (!reduxUlgMapConfig.chartType) return; // Nothing to restore

        // Restore chart type
        setChartType(reduxUlgMapConfig.chartType);

        // Restore type indicator
        if (reduxUlgMapConfig.indicator) {
            const ind = indicators.find(f => f.indicator_code === reduxUlgMapConfig.indicator);
            if (ind) {
                setTypeIndicator({
                    value: reduxUlgMapConfig.indicator,
                    label: `${ind.indicator_name} (${ind.indicator_date})`,
                });
                setTypeSubIndicators(ind.sub_indicators || []);

                // Restore sub-indicator
                if (reduxUlgMapConfig.subIndicator) {
                    const subInd = ind.sub_indicators?.find(
                        s => s.code === reduxUlgMapConfig.subIndicator
                    );
                    if (subInd) {
                        setSelectedTypeSubIndicator({
                            value: subInd.code,
                            label: subInd.name
                        });
                    }
                }
            }
        }

        // Restore selected locations
        if (reduxUlgMapConfig?.selectedLocation) {
            const firstLoc = reduxUlgMapConfig.selectedLocation;
            setLocalSelectedLocations({
                value: firstLoc.value,
                label: firstLoc.label,
                geo_level: firstLoc.geo_level
            });
        }

        setIsRestored(true);
    }, [reduxUlgMapConfig, indicators, isRestored]);

    // Filter indicators that are no longer available in the current workspace
    useEffect(() => {
        if (!indicators || indicators.length === 0) return;

        const availableCodes = new Set(indicators.map(i => i.indicator_code));

        // --- Type Indicator ---
        if (typeIndicator && !availableCodes.has(typeIndicator.value)) {
            setTypeIndicator(null);
            setSelectedTypeSubIndicator(null);
        }
    }, [indicators, typeIndicator]);

    // Handle type indicator change
    const handleTypeIndicator = (selected) => {
        setTypeIndicator(selected);
        if (selected) {
            const ind = indicators.find(f => f.indicator_code === selected.value);
            setTypeSubIndicators(ind?.sub_indicators || []);
            setSelectedTypeSubIndicator(null);
        } else {
            setTypeSubIndicators([]);
            setSelectedTypeSubIndicator(null);
        }
    };

    // Handle chart type change
    const handleChangeChartType = (e) => {
        const value = e.target.value;
        setChartType(value);
        setBool(true);
        setSelectedTypeSubIndicator(null);
        setTypeIndicator(null);
    };

    // Handle reset
    const handleReset = () => {
        setLocalSelectedLocations([]);
        setChartType("bubble");
        setBool(true);
        setTypeIndicator(null);
        setTypeSubIndicators([]);
        setSelectedTypeSubIndicator(null);
    };

    // ULG Map Submit Button Enable/Disable Logic
    useEffect(() => {
        const isIndicatorSelected = Boolean(typeIndicator);
        const isLocationSelected = (Array.isArray(localSelectedLocations) && localSelectedLocations.length > 0) || Boolean(localSelectedLocations);

        setIsUlgMapSubmitDisabled(!(isIndicatorSelected && isLocationSelected));
    }, [typeIndicator, localSelectedLocations]);

    // ✅ Submit functionality - Prepare both formattedPayload AND reduxPayload
    const handleSubmit = (e) => {
        e.preventDefault();

         locations = [
            //{
        //     geo_level: 1,
        //     lgd_name: "India (National)",
        //     lgd_code: 1000
        // },
         ...locations];

        const selectedFeatures = locations.filter((f) =>
            Array.isArray(localSelectedLocations)
                ? localSelectedLocations.some(
                    (loc) => loc.value === f.properties?.code || loc.lgd_code || loc.code
                )
                : localSelectedLocations?.value === f.properties?.code || f.lgd_code || f.code
        );

        // ✅ KEEP THE ORIGINAL formattedPayload (for ULG map visualization)
        const formattedPayload = {
            chartType,
            selectedVisualizations: { value: "ulgmap", label: "ULG Map" },
            selectedLocation:localSelectedLocations,
            indicator: {
                code: typeIndicator?.value,
                label: typeIndicator?.label,
                subIndicator: selectedTypeSubIndicator
                    ? {
                        code: selectedTypeSubIndicator.value,
                        label: selectedTypeSubIndicator.label,
                    }
                    : {},
            },
            selectedLocations: selectedFeatures.length > 0
                ? selectedFeatures.map(loc => ({
                    geo_level: loc.properties?.geo_level_code || loc.geo_level || loc.geo_level_code,
                    lgd_name: loc.properties?.name || loc.name || loc.lgd_name,
                    lgd_code: loc.properties?.code || loc.id || loc.code,
                }))
                : [{
                    geo_level: 1,
                    lgd_name: "India (National)",
                    lgd_code: 1000
                }],
                
            indicator_info: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
        };

        // ✅ NEW: Create simplified Redux payload (for restoration)
        const reduxPayload = {
            chartType,
            indicator: typeIndicator?.value || null,
            subIndicator: selectedTypeSubIndicator?.value || null,
            selectedLocation:localSelectedLocations,
            selectedLocations: selectedFeatures.length > 0
                ? selectedFeatures.map(loc => ({
                    geo_level: loc.properties?.geo_level_code || loc.geo_level || loc.geo_level_code,
                    lgd_name: loc.properties?.name || loc.name || loc.lgd_name,
                    lgd_code: loc.properties?.code || loc.id || loc.code,
                }))
                : [{
                    geo_level: 1,
                    lgd_name: "India (National)",
                    lgd_code: 1000
                }],
            fullIndicatorInfo: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
        };

        const baseIndicators = [{
            i_code: typeIndicator?.value,
            si_code: chartType === "pie"
                ? "all"
                : (selectedTypeSubIndicator?.value || "")
        }];

        const lgd_geo_level = localSelectedLocations?.geo_level || 1;

        const payload = {
            lgd_code: localSelectedLocations?.value || 1000,
            lgd_geo_level: lgd_geo_level,
            geo_level_code: lgd_geo_level + 1,
            indicators: baseIndicators,
        };

        // ✅ Send both payloads to parent
        onSubmit({ formattedPayload, payload, reduxPayload });
    };

    return (
        <div className="cardbg p-3">
            <Form.Label>Map Configuration - ULG</Form.Label>
            <div className="col-md-6">
                <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Select
                        options={[
                            { label: "India (National)", value: 1000, geo_level: 1 },
                            ...locations.filter(f => (f.properties?.geo_level_code || f.geo_level_code ||f.geo_level )!== 4).map(f => ({
                                value: f.properties?.code || f.id,
                                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.properties?.geo_level_code || f.geo_level || f.geo_level_code || 4)})`,
                                geo_level: f.properties?.geo_level_code || f.geo_level_code || f.geo_level || 4,
                            }))
                        ]}
                        onChange={setLocalSelectedLocations}
                        value={localSelectedLocations}
                        placeholder="Choose Location"
                    />
                </Form.Group>
            </div>

            <h5>Type</h5>
            <div className="d-flex mb-3">
                <Form.Check
                    type="radio"
                    label="Bubble"
                    value="bubble"
                    checked={chartType === "bubble"}
                    onChange={handleChangeChartType}
                    className="me-3"
                />
                <Form.Check
                    type="radio"
                    label="Marker"
                    value="marker"
                    checked={chartType === "marker"}
                    onChange={handleChangeChartType}
                    className="me-3"
                />
                <Form.Check
                    type="radio"
                    label="Pie"
                    value="pie"
                    checked={chartType === "pie"}
                    onChange={handleChangeChartType}
                />
            </div>

            {isBool && (
                <div className="row mb-3">
                    <div className="col-md-6">
                        <Form.Group>
                            <Form.Label>
                                {chartType.charAt(0).toUpperCase() + chartType.slice(1)} Indicator
                            </Form.Label>
                            <Select
                                options={
                                    chartType === "pie"
                                        ? indicators
                                            .filter(
                                                (f) => f.measurement_type !== "Categorical - Nominal - Text" && 
                                                Array.isArray(f.sub_indicators) && 
                                                f.sub_indicators.length > 0 && 
                                                f.sub_indicators.some(sub => sub.name && sub.name !== 'NA') &&
                                                f.geo_levels.includes('ULG')
                                            )
                                            .map((f) => ({
                                                value: f.indicator_code,
                                                label: `${f.indicator_name} (${f.indicator_date})`,
                                            }))
                                        : indicators
                                            .filter(f => f.measurement_type !== "Categorical - Nominal - Text" &&
                                                f.geo_levels?.includes('ULG')
                                            )
                                            .map((f) => ({
                                                value: f.indicator_code,
                                                label: `${f.indicator_name} (${f.indicator_date})`,
                                            }))
                                }
                                value={typeIndicator}
                                onChange={handleTypeIndicator}
                                classNamePrefix="select"
                                placeholder={`Choose ${chartType} indicator`}
                                noOptionsMessage={noOptionsMessage}
                            />
                        </Form.Group>
                    </div>

                    {typeIndicator && (
                        <div className="col-md-1 text-end">
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                    setTypeIndicator(null);
                                    setSelectedTypeSubIndicator(null);
                                }}
                                className="mt-4"
                            >
                                <MdOutlineDeleteOutline />
                            </Button>
                        </div>
                    )}

                    {chartType !== "pie" && typeIndicator && typeSubIndicators.length > 0 && (
                        <div className="col-md-5">
                            <Form.Group>
                                <Form.Label>
                                    {chartType.charAt(0).toUpperCase() + chartType.slice(1)} Sub Indicator
                                </Form.Label>
                                <Select
                                    options={typeSubIndicators.map((s) => ({
                                        value: s.code,
                                        label: s.name,
                                    }))}
                                    value={selectedTypeSubIndicator}
                                    onChange={setSelectedTypeSubIndicator}
                                    classNamePrefix="select"
                                    placeholder={`Choose ${chartType} sub indicator`}
                                    noOptionsMessage={noOptionsMessage}
                                />
                            </Form.Group>
                        </div>
                    )}
                </div>
            )}

            <div className="row mt-2">
                <div className="col-md-8 text-start">
                    <p style={{ fontSize: '0.85em' }}>
                        <i>Note:</i> Indicators added to the workspace are listed in the dropdown list.
                        To include additional indicators, go to the Home page and add the required indicators to the workspace.
                    </p>
                </div>
                <div className="text-end col-md-4">
                    <Button variant="secondary" onClick={handleReset} className="btn-sm">
                        Reset
                    </Button>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Button onClick={handleSubmit} className="btn-sm btn-primary" disabled={isUlgMapSubmitDisabled}>
                        Submit
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UlgMapVisualization;